import apiService from "./apiService";

/**
 * Alcune route configurator restituiscono uno scalare; altre un oggetto tipo { ORARIOBLOCCO: "12,30" }.
 */
function unwrapConfiguratorOrarioResponse(response, candidateKeys) {
  if (response == null) return null;
  if (typeof response === "number" && !Number.isNaN(response)) return response;
  if (typeof response === "string") {
    return response.trim() === "" ? null : response;
  }
  if (typeof response === "object" && !Array.isArray(response)) {
    const layers = [response, response.data].filter(
      (x) => x && typeof x === "object" && !Array.isArray(x)
    );
    for (const layer of layers) {
      for (const key of candidateKeys) {
        if (Object.prototype.hasOwnProperty.call(layer, key)) {
          const v = layer[key];
          if (v != null && String(v).trim() !== "") return v;
        }
      }
    }
  }
  return null;
}

/**
 * Servizio per ottenere le configurazioni dell'azienda
 */
class ConfigService {
  /**
   * Ottiene il logo dell'azienda
   * @returns {Promise<string>} URL del logo
   */

  async getLogo() {
    try {
      const response = await apiService('/configurator/logodit/true', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del logo:", error);
      return null;
    }
  }

  /**
   * Salva il logo dell'azienda
   * @param {string} logoBase64 - Logo in formato base64
   * @returns {Promise<Object>} Risultato del salvataggio
   */
  async setLogo(logoBase64) {
    try {
      const response = await apiService('/configurator/setlogo', 'POST', {
        logoBase64: logoBase64
      });
      return response;
    } catch (error) {
      console.error("Errore nel salvataggio del logo:", error);
      throw error;
    }
  }

  /**
   * Ottiene il nome dell'azienda
   * @returns {Promise<string>} Nome dell'azienda
   */
  async getNomeAzienda() {
    try {
      const response = await apiService('/configurator/nomedit', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del nome azienda:", error);
      return "Azienda";
    }
  }

  async getMastroCclienti() {
    try {
      const response = await apiService('/configurator/macli', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del masto clienti:", error);
      return "Azienda";
    }
  }

  /**
   * Ottiene la partita IVA dell'azienda
   * @returns {Promise<string>} Partita IVA
   */
  async getPartitaIVA() {
    try {
      let mastroClienti = localStorage.getItem('mastroClienti');
      if (mastroClienti) {
        return mastroClienti;
      }

      const response = await apiService('/configurator/pivadit', 'GET');
      
      if(response) {
        localStorage.setItem('mastroClienti', response);
      }
      
      return response;
    } catch (error) {
      console.error("Errore nel recupero della partita IVA:", error);
      return null;
    }
  }

  async getOrarioValido() {
    try {
      const response = await apiService('/configurator/orarioprodpane', 'GET');
      const unwrapped = unwrapConfiguratorOrarioResponse(response, [
        'ORARIOPRODPANE',
        'orarioprodpane',
        'value',
      ]);
      return unwrapped != null ? unwrapped : null;
    } catch (error) {
      console.error("Errore dell'orario:", error);
      return null;
    }
  }

  async getOrarioBlocco() {
    try {
      const response = await apiService('/configurator/orarioblocco', 'GET');
      const unwrapped = unwrapConfiguratorOrarioResponse(response, [
        'ORARIOBLOCCO',
        'orarioblocco',
        'value',
      ]);
      return unwrapped != null ? unwrapped : null;
    } catch (error) {
      console.error("Errore nel recupero ORARIOBLOCCO:", error);
      return null;
    }
  }

  async getOrarioSblocco() {
    try {
      const response = await apiService('/configurator/orariosblocco', 'GET');
      const unwrapped = unwrapConfiguratorOrarioResponse(response, [
        'ORARIOSBLOCCO',
        'orariosblocco',
        'value',
      ]);
      return unwrapped != null ? unwrapped : null;
    } catch (error) {
      console.error("Errore nel recupero ORARIOSBLOCCO:", error);
      return null;
    }
  }

  async getGiornoChiusura() {
    try {
      const response = await apiService('/configurator/chiusuraditta1', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del giorno di chiusura:", error);
      return null;
    }
  }

  async getGiornoApertura() {
    try { 
      const response = await apiService('/configurator/chiusuraditta2', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del giorno di apertura:", error  );
      return null;    
    }
  } 

  async getSelettoreGruppo() {
    try { 
      const response = await apiService('/configurator/GRUPPOCOLORDEFAULT', 'GET');
      return response - 1 ; // Restituisce l'indice (0-based)
    } catch (error) {
      console.error("Errore nel recupero del selettore gruppo:", error  );
      return null;    
    }
  } 

  async getPanifyNoPlanning() {
    try {
      const response = await apiService('/configurator/panifynoplanning', 'GET');
      const val = (response && typeof response === 'object')
        ? (response.PANIFYNOPLANNING ?? response.panifynoplanning ?? response.value)
        : response;
      return Number(val) === 1 || val === true || val === '1';
    } catch (error) {
      console.error("Errore nel recupero PANIFYNOPLANNING:", error);
      return false;
    }
  } 


  /**
   * Ottiene tutte le configurazioni dell'azienda
   * @returns {Promise<Object>} Oggetto con le configurazioni
   */
  async getConfigurations() {
    try {
      const [logo, nome, piva, orario, orarioBlocco, giornoChiusura, giornoApertura] = await Promise.all([
        this.getLogo(),
        this.getNomeAzienda(),
        this.getPartitaIVA(),
        this.getOrarioValido(),
        this.getOrarioBlocco(),
        this.getGiornoChiusura(),
        this.getGiornoApertura()
      ]);
      
      return {
        logo,
        nome,
        piva,
        orario,
        orarioBlocco,
        giornoChiusura,
        giornoApertura
      };
    } catch (error) {
      console.error("Errore nel recupero delle configurazioni:", error);
      return {
        logo: null,
        nome: "Azienda",
        piva: null,
        orario: null,
        orarioBlocco: null,
        giornoChiusura: null,
        giornoApertura: null
      };
    }
  }


/** destinazioni */

  /**
   * Ottiene le destinazioni filtrate per email
   * @param {string} email - Email da cercare
   * @returns {Promise<Array>} Array delle destinazioni
   */
  async getDestinazioniByEmail(email) {
    try {
      const url = `/destinazioni?destlogin=${encodeURIComponent(email)}`;
      const response = await apiService(url, "GET");
      return Array.isArray(response) ? response : response.data || [];
    } catch (error) {
      console.error(`Errore nel recupero delle destinazioni per email ${email}:`, error);
      throw error;
    }
  }

 async getDestinazioni() {
    try {
      const url = `/destinazioni`;
      const response = await apiService(url, "GET");

      // Restituisce tutti i record senza filtri
      return Array.isArray(response) ? response : response.data || [];
    } catch (error) {
      console.error(`Errore nel recupero delle destinazioni`, error);
        throw error;
    }
  }


  async getPreordini(body = null) {
    try {
      const url = `/applicazioni/panificatori/preorder`;
      
      // Se viene passato un body, usa POST, altrimenti GET
      const response = await apiService(url, "POST", body);
      console.log("Risposta POST completa:", response);
      return response;
     
    } catch (error) {
      console.error(`=== ERRORE getPreordini ===`);
      console.error("Messaggio:", error.message);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      console.error("Response headers:", error.response?.headers);
      console.error("Body inviato:", JSON.stringify(body, null, 2));
      throw error;
    }
  }

  async getSmistamento(body) {
    const url = `/applicazioni/panificatori/smistamento`;
    return await apiService(url, "POST", body);
  }

  async postSmistamentoCompleted(body) {
    const url = `/applicazioni/panificatori/smistamento/completed`;
    return await apiService(url, "POST", body);
  }



  /**
   * Ottiene il cliente interno di default
   * @returns {Promise<string>} Codice cliente interno
   */
  async getDefaultClienteInterno() {
    try {
      const response = await apiService('/configurator/def_clie_interno', 'GET');
      return response;
    } catch (error) {
      console.error("Errore nel recupero del cliente interno:", error);
      return null;
    }
  }


  /**
   * Genera una password casuale secondo i criteri specificati
   * @returns {string} Password generata
   */
  generateRandomPassword() {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ" + 
                  "abcdefghijklmnopqrstuvwxyz" + 
                  "0123456789" + 
                  "()?!/\\:;.,=-_#@&%+*|";
    
    let password = "";
    for (let i = 0; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  /**
   * Aggiorna la password di una destinazione
   * @param {string} email - Email della destinazione
   * @param {string} newPassword - Nuova password
   * @returns {Promise<Object>} Risultato dell'operazione
   */
  async updatePassword(email, newPassword) {
    try {
      // Prima verifica se l'email esiste nelle destinazioni
      const destinazioni = await this.getDestinazioniByEmail(email);
      
      if (!destinazioni || destinazioni.length === 0) {
        throw new Error("Email non trovata nel database");
      }

      const destinazione = destinazioni[0];
      const url = `/configurator/clienti/${destinazione.codcliente}/destinazioni/${destinazione.coddestinazione}`;
      
      const response = await apiService(url, 'POST', {
        destpsw: newPassword
      });
      
      return response;
    } catch (error) {
      console.error("Errore nell'aggiornamento della password:", error);
      throw error;
    }
  }

  /**
   * Invia email di recupero password
   * @param {string} email - Email destinatario
   * @param {string} newPassword - Nuova password
   * @returns {Promise<Object>} Risultato dell'invio
   */
  async sendPasswordRecoveryEmail(email, newPassword) {
    try {
      const response = await apiService('/configurator/send-password-email', 'POST', {
        email: email,
        newPassword: newPassword
      });
      return response;
    } catch (error) {
      console.error("Errore nell'invio dell'email:", error);
      throw error;
    }
  }

  /**
   * Recupera password completo: genera, salva e invia email
   * @param {string} email - Email per il recupero
   * @returns {Promise<Object>} Risultato dell'operazione
   */
  async recoverPassword(email) {
    try {
      // 1. Verifica che l'email esista
      const destinazioni = await this.getDestinazioniByEmail(email);
      
      if (!destinazioni || destinazioni.length === 0) {
        throw new Error("Email non trovata nel database");
      }

      // 2. Genera nuova password
      const newPassword = this.generateRandomPassword();

      // 3. Aggiorna la password nel database
      await this.updatePassword(email, newPassword);

      // 4. Invia email con la nuova password
      await this.sendPasswordRecoveryEmail(email, newPassword);

      return {
        success: true,
        message: "Nuova password inviata via email"
      };
    } catch (error) {
      console.error("Errore nel recupero password:", error);
      throw error;
    }
  }


  /**
   * Carica in memoria tutti i parametri di configurator 
   * da utilizzare per la compilazione del pannello di amministratore
   */

  async loadAllConfigurators() {
    try {
      const url = `/configurator`;
      const response = await apiService(url, 'GET');
      console.log('✅ Risposta caricamento configuratori:', response);
      return response;
    } catch (error) {
      console.error('❌ Errore nel caricamento dei dati di configurator:', error);
      throw error;
    }
  }

  /**
   * Aggiorna un parametro del configuratore
   * @param {string} nomec - Nome del parametro di configurazione
   * @param {string} valore - Valore da impostare
   * @returns {Promise<Object>} Risultato dell'aggiornamento
   */
  async updateConfigurator(nomec, valore) {
    try {
      console.log('⚙️ === AGGIORNAMENTO CONFIGURATORE ===');
      console.log('📝 Parametro:', nomec);
      console.log('💾 Valore:', valore);

      const url = '/configurator/update';
      const body = {
        nomec: nomec,
        valore: valore
      };

      console.log('📡 URL chiamata:', url);
      console.log('📦 Body inviato:', body);

      const response = await apiService(url, 'POST', body);

      console.log('✅ Risposta aggiornamento configuratore:', response);

      return response;
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento del configuratore:', error);
      throw error;
    }
  }


  /**
   * Aggiorna multipli parametri del configuratore
   * @param {object} bodyparam - Nome del parametro di configurazione
   * @returns {Promise<Object>} Risultato dell'aggiornamento
   */
  async updateMultiConfigurator(bodyparam) {
    try {

      const url = '/configurator/update-multi';
      const response = await apiService(url, 'POST', bodyparam);
      console.log('✅ Risposta aggiornamento configuratore:', response);

      return response;
    } catch (error) {
      console.error('❌ Errore nell\'aggiornamento del multi-configuratore:', error);
      throw error;
    }
  }





}

const configService = new ConfigService();
export default configService;